import { API, hasBackend } from './api';
import mockResponse from '@/data/mock-response.json';
import { toast } from 'react-toastify';
import { RagSource, Thread } from '@/models/thread';
import { parseFullMessage } from '@/utils/functions';
import { extractFileID, getWorkspaceFileURL } from '@/utils/files';

interface QueryData {
  response: string;
  thread_id: string;
  model_id: string;
  model_provider: string;
  sources: Array<RagSource>;
}

export interface FileStatus {
  mongodb_document_id: string;
  file_id: string;
  file_name: string;
  original_file_name: string;
  timestamp_ms: number;
  organization_id: string;
  user_id: string;
  source_type: string;
  upload_status: string;
  ingest_status: string;
  created_at: string;
  updated_at: string;
}

export const LLMService = {
  query: async (prompt: string, threadID?: string): Promise<QueryData> => {
    if (!hasBackend) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(mockResponse);
        }, 5000);
      });
    }
    const params: { query: string; thread_id?: string; new_thread?: boolean } =
      {
        query: prompt,
      };

    if (threadID) {
      params['thread_id'] = threadID;
    } else {
      params['new_thread'] = true;
    }
    const response = (await API.get('/thread/query-with-citations', {
      params,
    })) as {
      status: 'success' | 'error';
      data: QueryData;
    };
    return response.data;
  },
  getThreads: async (): Promise<Array<Thread>> => {
    const threads: Array<Thread> = [];
    if (!hasBackend) {
      return threads;
    }

    const response = (await API.get('/thread')) as {
      status: number;
      data: Array<{
        thread_id: string;
        created_at: string;
        thread_items: Array<{
          chat_id: string;
          created_at: string;
          query: string;
          llm_response: string;
          rag_sources: Array<RagSource>;
        }>;
      }>;
    };

    if (response.status !== 200) {
      throw new Error('Failed to fetch chats');
    } else {
      response.data.forEach((thread) => {
        const { thread_id, thread_items } = thread;

        if (thread_items.length === 0) {
          return;
        }
        threads.push({
          id: thread_id,
          title: thread_items[0].query,
          items: thread_items.map((item) => ({
            id: item.chat_id,
            disliked: false,
            prompt: item.query,
            queryDate: new Date(item.created_at),
            response: parseFullMessage(item.llm_response, item.rag_sources),
            enabledCitation: false,
            sources: item.rag_sources.map((source) => ({
              source_id: source.source_id,
              file_name: source.file_name,
              title: source.file_name,
              url: getWorkspaceFileURL(extractFileID(source.file_id)), // Placeholder for URL
              logo: '', // Placeholder for logo
            })),
          })),
          filters: {
            countries: [],
            sources: [],
          },
        });
      });
    }

    return threads;
  },
  getSharepointStatus: async (): Promise<boolean> => {
    try {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 5000);
      });
      // const response = (await API.get('/sharepoint/status')) as {
      //   status: number;
      //   data: {
      //     connected: boolean;
      //   };
      // };

      // if (response.status === 200) {
      //   return response.data.connected;
      // } else {
      //   return false;
      // }
    } catch (error) {
      console.error('Error fetching SharePoint status:', error);
      return false;
    }
  },
  sendSharepointCode: async (code: string): Promise<boolean> => {
    try {
      return new Promise((resolve) => {
        setTimeout(() => {
          console.log('Pretending to send code to backend:', code);
          resolve(true);
        }, 10000);
      });
      // const response = (await API.post('/sharepoint/connect', {
      //   code,
      // })) as {
      //   status: number;
      // };

      // if (response.status === 200) {
      //   toast.success('Successfully connected to SharePoint!');
      //   return true;
      // } else {
      //   toast.error('Failed to connect to SharePoint.');
      //   return false;
      // }
    } catch (error) {
      console.error('Error sending SharePoint code:', error);
      toast.error(
        (error as Error).message ||
          'An error occurred while connecting to SharePoint.'
      );
      return false;
    }
  },
};
